import { guidFor } from '@ember/object/internals';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismCard, PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import type { SupportedCurrencies } from 'frontend-utils/utils/constants/currency-mapping-constants';
import GeneralSkeleton from 'lumen/components/general/skeleton';
import changeColorClass from 'lumen/helpers/change-color-class';
import { type Variance, VARIANCES } from 'lumen/utils/constants/variance';
import { formatVariance } from 'lumen/utils/market-snapshots/variance';

export interface MarketSnapshotsKpiTotal {
  format: 'currency' | 'decimal' | 'percent';
  value: number | null;
}

interface MarketSnapshotsKpiCardSignature {
  Args: {
    // Used only when the total's format is "currency".
    currency: SupportedCurrencies;
    isLoading?: boolean;
    title: string;
    total: MarketSnapshotsKpiTotal;
    // A fraction for percentage variances, the KPI's own unit for absolute
    // ones. Null hides the line.
    variance: number | null;
    varianceLabel: string;
    varianceType: Variance;
  };
}

interface VarianceLine {
  arrowIcon: 'arrow-down' | 'arrow-up' | undefined;
  colorClass: string;
  formattedVariance: string;
}

function arrowIcon(variance: number): VarianceLine['arrowIcon'] {
  if (variance === 0) {
    return undefined;
  }
  return variance > 0 ? 'arrow-up' : 'arrow-down';
}

export default class MarketSnapshotsKpiCard extends Component<MarketSnapshotsKpiCardSignature> {
  @service declare intl: Services['intl'];

  titleId = `kpi-card-title-${guidFor(this)}`;

  get formattedValue(): string | null {
    const { currency, total } = this.args;
    if (total.value === null) {
      return null;
    }
    return this.intl.formatNumber(total.value, {
      currency: total.format === 'currency' ? currency : undefined,
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
      style: total.format,
    });
  }

  get varianceLine(): VarianceLine | null {
    const { total, variance, varianceType } = this.args;
    if (variance === null || varianceType === VARIANCES.NONE) {
      return null;
    }
    return {
      arrowIcon: arrowIcon(variance),
      colorClass: changeColorClass(variance),
      formattedVariance: formatVariance(this.intl, variance, {
        isValuePercent: total.format === 'percent',
        varianceType,
      }),
    };
  }

  <template>
    <PrismCard
      aria-labelledby={{this.titleId}}
      data-test-id="kpi-card"
      role="group"
    >
      <:header as |Header|>
        <Header.title id={{this.titleId}}>{{@title}}</Header.title>
      </:header>

      <:content>
        {{#if @isLoading}}
          <div
            class="flex items-center justify-between"
            data-test-id="kpi-card-skeleton"
          >
            <GeneralSkeleton class="rounded-100 h-300 w-1/4" />
            <GeneralSkeleton class="rounded-100 h-200 w-1/3" />
          </div>
        {{else}}
          <div class="flex items-center justify-between gap-200">
            <span
              class="text-500--semi-bold text-neutral-emphasis truncate"
              data-test-id="kpi-card-value"
            >
              {{#if this.formattedValue}}
                {{this.formattedValue}}
              {{else}}
                <span aria-hidden="true">{{t "general.missing"}}</span>
                <span class="sr-only">{{t "general.no_data"}}</span>
              {{/if}}
            </span>

            {{#if this.varianceLine}}
              <div
                class="text-200--regular flex items-center
                  {{this.varianceLine.colorClass}}"
                data-test-id="kpi-card-variance"
              >
                {{#if this.varianceLine.arrowIcon}}
                  <PrismIcon
                    @color="inherit"
                    @name={{this.varianceLine.arrowIcon}}
                    aria-hidden="true"
                    data-test-id="kpi-card-variance-arrow"
                  />
                {{/if}}
                <span>{{this.varianceLine.formattedVariance}}
                  {{@varianceLabel}}</span>
              </div>
            {{/if}}
          </div>
        {{/if}}
      </:content>
    </PrismCard>
  </template>
}
